import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Car,
  Users,
  Package,
  DollarSign,
  Clock,
  Edit,
  ToggleLeft,
  ToggleRight,
  Calendar,
  User,
  Activity
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { toast } from 'sonner';
import { useVehicleCategory, useToggleVehicleCategoryStatus } from '@/api-services/hooks/useVehicleCategory';
import { VehicleCategory } from '@/api-services/hooks/vehicleCategoryTypes';

const ViewVehicleCategoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { categoryId } = useParams<{ categoryId: string }>();
  const categoryIdNum = categoryId ? parseInt(categoryId, 10) : 0;

  // Fetch vehicle category data
  const { data: categoryData, loading, error, refetch } = useVehicleCategory(
    categoryIdNum,
    {
      onError: (error) => {
        toast.error(`Failed to load vehicle category: ${error.message}`);
      },
    }
  );

  // Toggle status hook
  const { mutate: toggleStatus, isLoading: isToggling } = useToggleVehicleCategoryStatus({
    onSuccess: (data, categoryId) => {
      toast.success('Vehicle category status updated successfully!');
      refetch(); // Refresh the data
    },
    onError: (error, categoryId) => {
      toast.error(`Failed to update vehicle category status: ${error.message}`);
    },
  });

  const handleToggleStatus = () => {
    if (categoryData) {
      toggleStatus(categoryData.vehicleCategoryId);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }) + ' at ' + date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getCurrencyDisplay = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading vehicle category details...</p>
        </div>
      </div>
    );
  }

  if (error || !categoryData) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <Car className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Vehicle Category Not Found</h3>
            <p className="text-muted-foreground mb-4">
              {error?.message || 'The vehicle category you are looking for does not exist.'}
            </p>
            <Button onClick={() => navigate('/admin/vehicle-categories')}>
              Back to Vehicle Categories
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/vehicle-categories')}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              {categoryData.vehicleCategoryName || 'Unknown Category'}
            </h1>
            <p className="text-muted-foreground mt-1">
              Vehicle category details and configuration
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate(`/admin/vehicle-categories/edit/${categoryData.vehicleCategoryId}`)}
            className="gap-2"
          >
            <Edit className="w-4 h-4" />
            Edit
          </Button>
          <Button
            variant={categoryData.isActive ? "destructive" : "default"}
            onClick={handleToggleStatus}
            disabled={isToggling}
            className="gap-2"
          >
            {isToggling ? (
              'Updating...'
            ) : categoryData.isActive ? (
              <>
                <ToggleLeft className="w-4 h-4" />
                Deactivate
              </>
            ) : (
              <>
                <ToggleRight className="w-4 h-4" />
                Activate
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Information Card */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Car className="w-5 h-5" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={categoryData.vehicleIcon} alt={categoryData.vehicleCategoryName} />
                  <AvatarFallback className="bg-blue-100 text-blue-800">
                    <Car className="h-8 w-8" />
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold">{categoryData.vehicleCategoryName}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className={categoryData.isActive ? "bg-success text-success-foreground" : "bg-muted text-muted-foreground"}>
                      {categoryData.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                    <Badge variant="outline">ID: {categoryData.vehicleCategoryId}</Badge>
                  </div>
                </div>
              </div>

              {categoryData.vehicleDescription && (
                <div>
                  <h4 className="font-medium mb-2">Description</h4>
                  <p className="text-muted-foreground">{categoryData.vehicleDescription}</p>
                </div>
              )}

              {categoryData.vehicleIcon && (
                <div>
                  <h4 className="font-medium mb-2">Vehicle Icon</h4>
                  <div className="flex items-center gap-2">
                    <img
                      src={categoryData.vehicleIcon}
                      alt="Vehicle Icon"
                      className="h-12 w-12 object-cover rounded"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                    <span className="text-sm text-muted-foreground truncate max-w-xs">
                      {categoryData.vehicleIcon}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Capacity and Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Capacity & Settings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium">Max Passengers</p>
                    <p className="text-2xl font-bold text-blue-600">{categoryData.maxPassengerCount}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Package className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-medium">Max Luggage Volume</p>
                    <p className="text-2xl font-bold text-orange-600">{categoryData.maxLuggageVolume}L</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Clock className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium">Wait Time Limit</p>
                    <p className="text-2xl font-bold text-purple-600">{categoryData.waitTimeLimit} min</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <Activity className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-medium">Status</p>
                    <p className="text-2xl font-bold text-indigo-600">{categoryData.isActive ? 'Active' : 'Inactive'}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Pricing Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Pricing
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Base Fare</span>
                <span className="font-semibold text-green-600">{getCurrencyDisplay(categoryData.baseFare)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Rate per KM</span>
                <span className="font-semibold text-blue-600">{getCurrencyDisplay(categoryData.ratePerKm)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Rate per Minute</span>
                <span className="font-semibold text-orange-600">{getCurrencyDisplay(categoryData.ratePerMinute)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Cancellation Fee</span>
                <span className="font-semibold text-red-600">{getCurrencyDisplay(categoryData.cancellationFee)}</span>
              </div>
            </CardContent>
          </Card>

          {/* System Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                System Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Calendar className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium">Created Date</p>
                    <p className="text-sm text-muted-foreground">{formatDate(categoryData.createdDate)}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <User className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium">Created By</p>
                    <p className="text-sm text-muted-foreground">{categoryData.createdBy || 'N/A'}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Calendar className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium">Modified Date</p>
                    <p className="text-sm text-muted-foreground">{formatDate(categoryData.modifiedDate)}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <User className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium">Modified By</p>
                    <p className="text-sm text-muted-foreground">{categoryData.modifiedBy || 'N/A'}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Activity className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium">Deleted</p>
                    <p className="text-sm text-muted-foreground">{categoryData.isDeletedValue ? 'Yes' : 'No'}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ViewVehicleCategoryPage;